import type { ReactNode } from 'react'

export interface RouteConfig {
  title?: string
  leftNote?: string | ReactNode
  breadcrumbs?: Array<{ label: string; href?: string }>
  useAppShell?: boolean
}

// Route configuration mapping
export const routeConfigs: Record<string, RouteConfig> = {
  '/dashboard': {
    title: 'Dashboard',
    useAppShell: true
  },
  '/ai-analysis': {
    title: 'Performance Analysis & Visualization',
    useAppShell: true
  },
  '/ai-generated-reports': {
    title: 'Performance Analysis Reports',
    useAppShell: true,
    breadcrumbs: [
      { label: 'Performance Analysis', href: '/ai-analysis' },
      { label: 'Generated Reports' }
    ]
  },
  '/ai-analysis/reports': {
    title: 'Performance Analysis Reports',
    useAppShell: true,
    breadcrumbs: [
      { label: 'Performance Analysis', href: '/ai-analysis' },
      { label: 'Reports' }
    ]
  },
  '/ai-analysis/visualization': {
    title: 'Data Visualization',
    useAppShell: true,
    breadcrumbs: [
      { label: 'AI Analysis', href: '/ai-analysis' },
      { label: 'Visualization' }
    ]
  },
  '/chatbot': {
    leftNote: 'Chat with your data',
    useAppShell: true
  },
  '/onboarding': {
    title: 'Onboarding',
    useAppShell: true
  },
  '/profile': {
    title: 'Profile Settings',
    useAppShell: true
  },
  '/settings': {
    title: 'Settings',
    useAppShell: true
  },
  '/subscription': {
    title: 'Subscription',
    useAppShell: true
  },
  '/subscription-plans': {
    title: 'Subscription Plans',
    useAppShell: true
  },
  '/support': {
    title: 'Support',
    useAppShell: true
  },
  '/tutorials': {
    title: 'Tutorials',
    useAppShell: true
  },
  '/demo': {
    title: 'Demo Mode',
    useAppShell: true
  },
  '/data': {
    title: 'Data Management',
    useAppShell: true
  },
  '/notifications': {
    title: 'Notifications',
    useAppShell: true
  },
  '/security': {
    title: 'Security Settings',
    useAppShell: true
  }
}

/**
 * Get route configuration based on pathname
 * Supports exact matches and parent route fallbacks
 */
export function getRouteConfig(pathname: string): RouteConfig {
  // Try exact match first
  if (routeConfigs[pathname]) {
    return routeConfigs[pathname]
  }

  // Try parent routes (for nested routes)
  const segments = pathname.split('/').filter(Boolean)
  for (let i = segments.length - 1; i > 0; i--) {
    const parentPath = '/' + segments.slice(0, i).join('/')
    if (routeConfigs[parentPath]) {
      return routeConfigs[parentPath]
    }
  }

  // Default configuration for authenticated routes
  return {
    useAppShell: true
  }
}

/**
 * Check if route should use AppShell layout
 */
export function shouldUseAppShell(pathname: string): boolean {
  // Public pages (landing page, terms, privacy) don't use AppShell
  if (pathname === '/' ||
    pathname === '/terms' ||
    pathname === '/privacy') {
    return false
  }

  // Auth routes don't use AppShell
  if (pathname.startsWith('/login') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/email-verification')) {
    return false
  }

  const config = getRouteConfig(pathname)
  return config.useAppShell !== false
}
